import React, { useState, useEffect, useMemo } from 'react';
import {
  Key,
  Plus,
  Eye,
  EyeOff,
  Copy,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Loader2,
  Calendar,
  Activity,
  FileText,
  Clock,
  Terminal,
  Settings,
  Code,
  Database
} from 'lucide-react';
import Button from '../components/Button';
import { developerApiService, ApiKey, DeveloperAccountStats, Recording, ApiLog } from '../services/developerApiService';
import SearchTemplates from './SearchTemplates';

interface DeveloperPortalProps {
  onBackToSettings?: () => void;
}

const DeveloperPortal: React.FC<DeveloperPortalProps> = ({ onBackToSettings }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // API Key Management State
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<ApiKey | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<number>>(new Set());

  // API Testing State
  const [testApiKey, setTestApiKey] = useState('');
  const [testResults, setTestResults] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [accountStats, setAccountStats] = useState<DeveloperAccountStats | null>(null);
  const [recordings, setRecordings] = useState<Recording[]>([]);


  // Developer Panel State
  const [isDeveloperPanelOpen, setIsDeveloperPanelOpen] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Activity className="h-4 w-4" /> },
    { id: 'api-keys', label: 'API Keys', icon: <Key className="h-4 w-4" /> },
    { id: 'search-templates', label: 'Search Templates', icon: <Database className="h-4 w-4" /> },
    { id: 'testing', label: 'API Testing', icon: <Code className="h-4 w-4" /> },
    { id: 'documentation', label: 'Documentation', icon: <FileText className="h-4 w-4" /> },
  ];

  // Fetch API keys on component mount
  useEffect(() => {
    fetchApiKeys();
  }, []);


  // Auto-dismiss messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchApiKeys = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const keys = await developerApiService.listApiKeys();
      setApiKeys(keys);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch API keys');
    } finally {
      setIsLoading(false);
    }
  };


  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      setError('Please enter a name for your API key');
      return;
    }

    try {
      setIsCreatingKey(true);
      setError(null);
      const newKey = await developerApiService.createApiKey({ name: newKeyName.trim() });
      setNewlyCreatedKey(newKey);
      setApiKeys(prev => [newKey, ...prev]);
      setNewKeyName('');
      setShowCreateForm(false);
      setSuccess('API key created successfully! Make sure to copy it now - it won\'t be shown again.');
    } catch (err: any) {
      setError(err.message || 'Failed to create API key');
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleDeleteApiKey = async (keyId: number, keyName: string) => {
    if (!window.confirm(`Are you sure you want to deactivate the API key "${keyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setError(null);
      await developerApiService.deleteApiKey(keyId);
      setApiKeys(prev => prev.filter(key => key.id !== keyId));
      setSuccess('API key deactivated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate API key');
    }
  };

  const toggleKeyVisibility = (keyId: number) => {
    setVisibleKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(keyId)) {
        newSet.delete(keyId);
      } else {
        newSet.add(keyId);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(`${label} copied to clipboard`);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleTestApi = async (endpoint: string) => {
    if (!testApiKey.trim()) {
      setError('Please enter an API key to test');
      return;
    }

    try {
      setIsTesting(true);
      setError(null);
      setTestResults(null);

      let result;
      switch (endpoint) {
        case 'health':
          result = await developerApiService.testApiKey(testApiKey);
          break;
        case 'stats':
          result = await developerApiService.getAccountStats(testApiKey);
          setAccountStats(result);
          break;
        case 'recordings':
          result = await developerApiService.getRecordings(testApiKey, { limit: 10 });
          setRecordings(result);
          break;
        default:
          throw new Error('Invalid endpoint');
      }

      setTestResults({ endpoint, data: result });
      setSuccess(`${endpoint} endpoint tested successfully`);
    } catch (err: any) {
      setError(err.message || `Failed to test ${endpoint} endpoint`);
    } finally {
      setIsTesting(false);
    }
  };

  const activeApiKeys = useMemo(() => apiKeys.filter(key => key.is_active), [apiKeys]);

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <div className="overflow-hidden rounded-lg bg-white shadow border border-gray-200">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Key className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active API Keys</dt>
                  <dd className="text-3xl font-semibold text-gray-900">{activeApiKeys.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {accountStats && (
          <>
            <div className="overflow-hidden rounded-lg bg-white shadow border border-gray-200">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FileText className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Recordings</dt>
                      <dd className="text-3xl font-semibold text-gray-900">{accountStats.total_recordings}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white shadow border border-gray-200">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Duration</dt>
                      <dd className="text-3xl font-semibold text-gray-900">{accountStats.total_duration_minutes.toFixed(1)}m</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Getting Started Section */}
      <div className="bg-white shadow border border-gray-200 sm:rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Quick Start Guide</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Get up and running with the ScribeDO API in minutes
          </p>
        </div>
        <div className="px-6 py-5">
          <div className="flow-root">
            <ul className="-mb-8">
              <li>
                <div className="relative pb-8">
                  <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                        <span className="text-sm font-medium text-white">1</span>
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5">
                      <p className="text-sm font-medium text-gray-900">Create an API Key</p>
                      <p className="text-sm text-gray-500">Generate your first API key to authenticate requests to our APIs</p>
                    </div>
                  </div>
                </div>
              </li>
              <li>
                <div className="relative pb-8">
                  <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                        <span className="text-sm font-medium text-white">2</span>
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5">
                      <p className="text-sm font-medium text-gray-900">Test Your Integration</p>
                      <p className="text-sm text-gray-500">Use our testing tools to verify your API key works correctly</p>
                    </div>
                  </div>
                </div>
              </li>
              <li>
                <div className="relative">
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                        <span className="text-sm font-medium text-white">3</span>
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 pt-1.5">
                      <p className="text-sm font-medium text-gray-900">Start Building</p>
                      <p className="text-sm text-gray-500">Integrate with your application using our comprehensive API</p>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow border border-gray-200 sm:rounded-lg">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Ready to get started?</h3>
              <p className="mt-1 text-sm text-gray-500">Create your first API key and begin integrating</p>
            </div>
            <Button
              onClick={() => setActiveTab('api-keys')}
              variant="primary"
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create API Key
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderApiKeys = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-normal text-gray-900">API keys</h2>
          <p className="mt-1 text-sm text-gray-600">
            Use API keys to authenticate requests to the ScribeDO API
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-[#635bff] border border-transparent rounded-md hover:bg-[#5a54f5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#635bff] transition-colors"
        >
          Create secret key
        </button>
      </div>

      {/* Show newly created key prominently */}
      {newlyCreatedKey && (
        <div className="bg-white border border-gray-300 rounded p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-sm font-medium text-gray-900">Your secret key</h3>
              <p className="mt-1 text-sm text-gray-600">
                Please save this secret key somewhere safe and accessible. For security reasons, you won't be able to view it again through your Stripe account.
              </p>
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-gray-900 select-all flex-1 pr-4">{newlyCreatedKey.key}</code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(newlyCreatedKey.key!, 'API key')}
                    className="flex-shrink-0 inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#635bff]"
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </button>
                </div>
              </div>
              <div className="mt-4 flex">
                <button
                  type="button"
                  onClick={() => setNewlyCreatedKey(null)}
                  className="text-sm text-gray-600 hover:text-gray-800 focus:outline-none"
                >
                  I have saved my secret key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create API Key Form */}
      {showCreateForm && (
        <div className="bg-white rounded border border-gray-300 p-6">
          <div className="pb-4 border-b border-gray-200">
            <h3 className="text-lg font-normal text-gray-900">Create a new secret key</h3>
            <p className="mt-1 text-sm text-gray-600">
              Your secret key will be displayed only once after creation
            </p>
          </div>
          <form onSubmit={handleCreateApiKey} className="pt-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="keyName" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  id="keyName"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#635bff] focus:border-[#635bff]"
                  placeholder="e.g., My integration"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewKeyName('');
                  }}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#635bff]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingKey}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-[#635bff] border border-transparent rounded-md hover:bg-[#5a54f5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#635bff] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingKey && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Create secret key
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-white border border-gray-300 rounded">
        {isLoading ? (
          <div className="px-6 py-8 text-center">
            <Loader2 className="h-6 w-6 text-gray-400 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-600">Loading...</p>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Key className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No API keys yet</h3>
            <p className="text-sm text-gray-600 mb-4">Create your first API key to get started.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-[#635bff] border border-transparent rounded-md hover:bg-[#5a54f5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#635bff] transition-colors"
            >
              Create secret key
            </button>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Secret key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last used
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {apiKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900">{key.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <code className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded font-mono">
                          {key.key_prefix}_{'•'.repeat(16)}...
                        </code>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(`${key.key_prefix}_${'*'.repeat(32)}`, 'API key reference')}
                          className="inline-flex items-center p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(key.created_at).split(',')[0]}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {key.last_used_at ? formatDate(key.last_used_at).split(',')[0] : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        type="button"
                        onClick={() => handleDeleteApiKey(key.id, key.name)}
                        className="text-red-600 hover:text-red-900 focus:outline-none"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );


  const renderTesting = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">API Testing</h3>
        <p className="text-sm text-gray-500">Test your API integration with live endpoints</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-medium text-gray-900 mb-4">API Key Authentication</h4>
        <div className="space-y-4">
          <div>
            <label htmlFor="testApiKey" className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <input
              type="text"
              id="testApiKey"
              value={testApiKey}
              onChange={(e) => setTestApiKey(e.target.value)}
              className="block w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="sk_your_api_key_here"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter your API key to test the endpoints below
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              onClick={() => handleTestApi('health')}
              disabled={isTesting}
              variant="secondary"
              className="flex items-center gap-2"
            >
              {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
              Test Health
            </Button>
            <Button
              onClick={() => handleTestApi('stats')}
              disabled={isTesting}
              variant="secondary"
              className="flex items-center gap-2"
            >
              {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
              Get Stats
            </Button>
            <Button
              onClick={() => handleTestApi('recordings')}
              disabled={isTesting}
              variant="secondary"
              className="flex items-center gap-2"
            >
              {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              List Recordings
            </Button>
          </div>
        </div>
      </div>

      {/* Test Results */}
      {testResults && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Test Results - {testResults.endpoint}</h4>
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm overflow-auto max-h-96">
            {JSON.stringify(testResults.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );

  const renderDocumentation = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">API Documentation</h3>
        <p className="text-sm text-gray-500">Complete reference for integrating with our API</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Authentication</h4>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">API Key Format</p>
              <code className="block bg-gray-50 p-2 rounded text-sm">
                Authorization: Bearer sk_your_api_key_here
              </code>
            </div>
            <div>
              <p className="text-sm text-gray-600">
                All API requests must include your API key in the Authorization header using Bearer authentication.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">Base URL</h4>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Production</p>
              <code className="block bg-gray-50 p-2 rounded text-sm">
                https://api.scribedo.com/api/developer-api/v1
              </code>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Development</p>
              <code className="block bg-gray-50 p-2 rounded text-sm">
                http://localhost:8000/api/developer-api/v1
              </code>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="font-medium text-gray-900 mb-4">Available Endpoints</h4>
        <div className="space-y-6">

          {/* Health Check */}
          <div className="border-l-4 border-green-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-mono">GET</span>
              <code className="text-sm">/health</code>
            </div>
            <p className="text-sm text-gray-600 mb-2">Test API key authentication and service health</p>
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-700 hover:text-gray-900">Example Response</summary>
              <pre className="bg-gray-50 p-3 rounded mt-2 text-xs overflow-auto">
{`{
  "status": "healthy",
  "user_id": 123,
  "message": "API key authentication successful"
}`}
              </pre>
            </details>
          </div>

          {/* Account Stats */}
          <div className="border-l-4 border-blue-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">GET</span>
              <code className="text-sm">/account/stats</code>
            </div>
            <p className="text-sm text-gray-600 mb-2">Get account statistics and usage information</p>
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-700 hover:text-gray-900">Example Response</summary>
              <pre className="bg-gray-50 p-3 rounded mt-2 text-xs overflow-auto">
{`{
  "user_id": 123,
  "email": "developer@example.com",
  "total_recordings": 45,
  "processed_recordings": 42,
  "failed_recordings": 3,
  "total_duration_minutes": 127.5,
  "account_type": "developer"
}`}
              </pre>
            </details>
          </div>

          {/* List Recordings */}
          <div className="border-l-4 border-purple-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-mono">GET</span>
              <code className="text-sm">/recordings</code>
            </div>
            <p className="text-sm text-gray-600 mb-2">List recordings with optional filtering</p>
            <p className="text-xs text-gray-500 mb-2">
              Query parameters: limit, offset, status
            </p>
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-700 hover:text-gray-900">Example Response</summary>
              <pre className="bg-gray-50 p-3 rounded mt-2 text-xs overflow-auto">
{`[
  {
    "id": "550e8400-e29b-41d4-a716-446655440000",
    "title": "Patient Consultation - John Doe",
    "status": "Processed",
    "duration": 420.5,
    "created_at": "2025-01-15T09:30:00Z",
    "media_type": "audio"
  }
]`}
              </pre>
            </details>
          </div>

          {/* Get Recording Details */}
          <div className="border-l-4 border-orange-500 pl-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-mono">GET</span>
              <code className="text-sm">/recordings/&#123;id&#125;</code>
            </div>
            <p className="text-sm text-gray-600 mb-2">Get detailed information about a specific recording</p>
            <details className="text-sm">
              <summary className="cursor-pointer text-gray-700 hover:text-gray-900">Example Response</summary>
              <pre className="bg-gray-50 p-3 rounded mt-2 text-xs overflow-auto">
{`{
  "id": "550e8400-e29b-41d4-a716-446655440000",
  "title": "Patient Consultation - John Doe",
  "filename": "consultation_20250115.wav",
  "status": "Processed",
  "duration": 420.5,
  "file_size": 8450000,
  "created_at": "2025-01-15T09:30:00Z",
  "media_type": "audio",
  "patient_name": "John Doe",
  "visit_type": "consultation",
  "preview": "Patient presents with chest pain..."
}`}
              </pre>
            </details>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Security Best Practices</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Store API keys securely and never expose them in client-side code</li>
              <li>• Use environment variables to manage API keys in your applications</li>
              <li>• Regenerate API keys periodically and when team members leave</li>
              <li>• Monitor API key usage and implement rate limiting in your applications</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full bg-[#fafbfc] flex flex-col">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between px-6 py-4">
          <h1 className="text-xl font-semibold text-gray-900">Developer Portal</h1>
          <div className="flex items-center space-x-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications */}
      {(success || error) && (
        <div className="px-6 pt-4 flex-shrink-0">
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
              <span className="text-green-800">{success}</span>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
              <span className="text-red-800">{error}</span>
            </div>
          )}
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        <div className="p-6 overflow-auto">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'api-keys' && renderApiKeys()}
          {activeTab === 'search-templates' && <SearchTemplates />}
          {activeTab === 'testing' && renderTesting()}
          {activeTab === 'documentation' && renderDocumentation()}
        </div>
      </div>
    </div>
  );
};

export default DeveloperPortal;